import { html, type TemplateResult } from 'lit';

// German abbreviations followed by a capitalized word that must NOT end a sentence.
// (Abbreviations before a digit — "Nr. 5", "ca. 3", "max. 5 bar" — need no entry: the
//  uppercase-only lookahead already refuses to split before a digit.)
const ABBREV =
  /\b(?:z\.\s?B|d\.\s?h|u\.\s?a|u\.\s?U|o\.\s?Ä|Dr|Prof|Nr|Pos|Str|Hr|Fr|Fa|Abt|bzw|bzgl|ggf|inkl|vgl|usw|Tel|Abb|bspw|Stk|mind)\./gi;

// One-dot-leader sentinel (U+2024): NOT a regex metachar, absent from [.!?]. Hides
// protected dots during the sentence split; every one is restored to '.' afterwards.
const DOT = '․';

/** Protect dots that are NOT sentence ends: German abbreviations + ordinal/date numbers ("am 3. März", "3. Stock"). */
function protectDots(s: string): string {
  return s
    .replace(ABBREV, m => m.replace(/\./g, DOT))
    .replace(/(\d)\.(?=[ \t])/g, (_m, d) => d + DOT);
}

/**
 * Break a ticket `description` into display lines. UI-side only — never mutates stored data.
 *  - strips a redundant leading "Problem:" (both views print their own "Problem" label/header)
 *  - splits on existing newlines FIRST, then on sentence boundaries (. ! ? + space + uppercase/quote),
 *    keeping the terminal punctuation; decimals, "Nr. 5", "ca. 3", German abbreviations and
 *    ordinals/dates ("am 3. März") are NOT split.
 *  - '' | '-' | whitespace | null | undefined  ->  []  (caller renders "-").
 * No regex lookbehind (parse-safe on all runtimes). No throw. No HTML produced (render layer escapes).
 */
export function splitDescriptionLines(desc: string | null | undefined): string[] {
  if (desc == null) return [];
  const text = String(desc).replace(/^\s*problem\s*:\s*/i, '').trim();
  if (text === '' || text === '-') return [];

  const lines: string[] = [];
  for (const segment of text.split(/\r?\n+/)) {
    const marked = protectDots(segment).replace(/([.!?]+)[ \t]+(?=[A-ZÄÖÜ"'(„«»])/g, '$1\n');
    for (const piece of marked.split('\n')) {
      const line = piece.split(DOT).join('.').trim();
      if (line) lines.push(line);
    }
  }
  return lines.length ? lines : [text.split(DOT).join('.')];
}

/**
 * Render a description as a stacked, one-line-per-sentence block. XSS-safe (Lit text bindings only —
 * never unsafeHTML). @param opts.class extra Tailwind classes on the wrapper.
 */
export function renderStructuredDescription(
  desc: string | null | undefined,
  opts: { class?: string } = {},
): TemplateResult {
  const lines = splitDescriptionLines(desc);
  if (lines.length === 0) return html`<span class="text-base-content/60">-</span>`;
  // Number the lines (1. 2. 3. …) when there is more than one; a single line stays unnumbered.
  const numbered = lines.length > 1;
  return html`<div class="flex flex-col gap-0.5 whitespace-normal break-words ${opts.class ?? ''}">
    ${lines.map((line, i) => html`<div>${numbered ? `${i + 1}. ${line}` : line}</div>`)}
  </div>`;
}
